/**
 * "Prompt of the day" — an inspiring question that rotates daily to overcome
 * writer's block (the #1 friction point of writing a journal).
 *
 * Unlike TEMPLATES (`templates.ts`, which insert a multiline structure:
 * gratitude, three good things), a prompt is ONE short question. The catalog
 * lives in code (keys); visible text is translated in the UI from `key` with
 * Lingui.
 *
 * DETERMINISTIC date-based selection: every day with the same `YYYY-MM-DD`
 * resolves to the same prompt (stable across reloads), and the sequence rotates
 * through the ENTIRE catalog before repeating. Pure and testable.
 */

/** Prompt catalog keys (the text is translated in the UI). */
export const WRITING_PROMPTS = [
  "prompt.grateful_today",
  "prompt.made_you_smile",
  "prompt.learned_recently",
  "prompt.looking_forward",
  "prompt.proud_of",
  "prompt.let_go_of",
  "prompt.small_win",
  "prompt.someone_kind",
  "prompt.recharges_you",
  "prompt.challenge_faced",
  "prompt.favourite_moment",
  "prompt.tell_younger_self",
  "prompt.want_to_remember",
  "prompt.feeling_right_now",
  "prompt.goal_this_week",
  "prompt.beautiful_lately",
] as const;

export type WritingPromptKey = (typeof WRITING_PROMPTS)[number];

/**
 * Stable integer hash (32-bit FNV-1a) of a string. Deterministic and
 * dependency-free, for mapping a date to a catalog index.
 */
function hashString(value: string): number {
  let hash = 0x811c_9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    // FNV prime; force unsigned 32-bit arithmetic.
    hash = Math.imul(hash, 0x0100_0193) >>> 0;
  }
  return hash >>> 0;
}

/** Index of the prompt for `dateKey` (`YYYY-MM-DD`). */
export function promptIndexForDate(dateKey: string): number {
  return hashString(dateKey) % WRITING_PROMPTS.length;
}

/** Prompt of the day for `dateKey` (`YYYY-MM-DD`). */
export function promptOfTheDay(dateKey: string): WritingPromptKey {
  // WRITING_PROMPTS is never empty → the index always exists.
  return WRITING_PROMPTS[promptIndexForDate(dateKey)]!;
}

/**
 * Next prompt in the catalog (for the "another question" button): rotates in
 * stable order from the current one, traversing everything before repeating. If
 * the current prompt is unrecognized, it falls back to the prompt for `dateKey`.
 */
export function nextPrompt(current: string, dateKey: string): WritingPromptKey {
  const currentIndex = (WRITING_PROMPTS as readonly string[]).indexOf(current);
  if (currentIndex === -1) {
    return promptOfTheDay(dateKey);
  }
  const nextIndex = (currentIndex + 1) % WRITING_PROMPTS.length;
  return WRITING_PROMPTS[nextIndex]!;
}
